import { useState } from "react";
import { PhoneNumberUtil } from "google-libphonenumber";
import { PhoneInput as InternationalPhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { Field, FieldError, FieldLabel } from "./ui/field";
import { cn } from "@/utils/cn";

const DEFAULT_COUNTRY = "ae";

interface IProps {
  value: string;
  label?: string;
  disabled?: boolean;
  hideCountryCode?: boolean;
  error: string | undefined;
  setFieldValue: (value: string) => void;
  setFieldError?: (error: string | undefined) => void;
  onBlur?: () => void;
  styles?: {
    label?: string;
    container?: string;
    inputClassName?: string;
  };
  placeholder?: string;
  icon?: React.ReactNode;
}

export const PhoneInput = ({
  value,
  error,
  label,
  styles,
  disabled,
  setFieldError,
  setFieldValue,
  onBlur,
  hideCountryCode = false,
  placeholder,
  icon,
}: IProps) => {
  const [currentCountry, setCurrentCountry] = useState(DEFAULT_COUNTRY);
  const phoneUtil = PhoneNumberUtil.getInstance();

  const validatePhone = (phone: string) => {
    if (!setFieldError) return;
    if (phone.length < 6) {
      setFieldError(undefined);
      return;
    }
    try {
      const parsed = phoneUtil.parseAndKeepRawInput(
        phone,
        currentCountry.toUpperCase(),
      );
      const isValid = phoneUtil.isValidNumber(parsed);
      setFieldError(isValid ? undefined : "Please enter a valid phone number");
    } catch {
      setFieldError("Please enter a valid phone number");
    }
  };

  return (
    <Field className={styles?.container}>
      {label && <FieldLabel className={styles?.label}>{label}</FieldLabel>}
      <div className="relative">
        <InternationalPhoneInput
          value={value}
          defaultCountry={DEFAULT_COUNTRY}
          disabled={disabled}
          onChange={(phone, data) => {
            if (data?.country?.iso2) setCurrentCountry(data.country.iso2);
            setFieldValue(phone);
          }}
          inputProps={{
            onBlur: () => {
              validatePhone(value);
              onBlur?.();
            },
          }}
          className="h-12!"
          inputClassName={cn(
            "w-full h-12! border border-input",
            icon && "pe-9!",
            styles?.inputClassName,
          )}
          countrySelectorStyleProps={{
            buttonClassName: "h-12! px-2 flex items-center",
            flagClassName: "rounded-xs",
            buttonContentWrapperClassName: "px-3 flex items-center gap-2",
            dropdownStyleProps: {
              listItemFlagClassName: "rounded-xs",
            },
          }}
          hideDropdown={hideCountryCode}
          placeholder={placeholder}
        />
        {icon && (
          <span className="absolute end-2.5 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
            {icon}
          </span>
        )}
      </div>
      <FieldError>{error}</FieldError>
    </Field>
  );
};
